import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Table from '../../../components/table';
import InitialValuesForm from '../../../constants/initialValues';
import { GrantManagementHeader } from '../../../constants/table-headers/grantManagementHeader';
import {
  useBoolean,
  useGetCustomerCompanyName,
  useGetGrantList,
  useGetGrantPlanNameList,
  useGetGrantPlanTypeList,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneAwardApi,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { GrantService } from '../../../services/wealthlane-award-services';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import GrantManagementModal from './grantModal';
import { SUPPORTED_FORMATS } from '../../../constants/common';
import { getFileExtension, handleOpenFile } from '../../../utils/global';
import { SettingSystemUserButton, SettingSystemUserHeader } from '../system-users/systemUserStyled';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import FormSelect from '../../../components/dropdown/formSelect';
import { GoBackBlock, ResetFilterWrapper } from '../../../constants/style/common.styled';
import ToastComponent from '../../../components/toast';
import { toast } from 'react-toastify';
import AlertPopup from '../../../components/alert-popup';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import Filter from '../../../components/filter';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';
import Button from '../../../components/button';
import { GrantFilterService } from '../../../services/wealthlane-award-services/services/GrantFilterService';

const initialValues = {
  data: InitialValuesForm.grantManagement,
  id: '',
  planId: '',
};

const GrantManagement = (): JSX.Element => {
  const { handleRequest, error: exception } = useWealthlaneAwardApi();
  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: planID } = useParams();
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [initialData, setInitialData] = useState({
    ...initialValues,
    planId: planID,
  });
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
  } = useBoolean(false);
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo, isSelectAllActive, uncheckedIds } = useDynamicTable();

  useEffect(() => {
    setTableInfo({ ...InitialValuesForm.tableParams, planId: state?.planId ?? '' });
  }, []);

  const [grantType, setGrantType] = useState('');
  const { state }: any = useLocation();
  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'GRANTS_MANAGEMENT_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const [planName, setPlanName] = useState(state?.planId ?? '');
  const [loading, setLoading] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [checkedValues, setCheckedValues] = useState<any>([]);
  const [checkedItems, setCheckedItems] = useState<any>([]);
  const [taskReferenceNo, setTaskReferenceNo] = useState<string>();
  const {
    value: isSubmitOpen,
    setFalse: closeSubmitModal,
    setTrue: openSubmitModal,
  } = useBoolean(false);

  const { data: planNames } = useGetGrantPlanNameList(handleRequest);
  const userAuthList = useSelector(selectAuthRoutesList);
  const [headerList, setHeaderList] = useState<string[]>([]);

  const { data: grantList, status } = useGetGrantList(
    handleRequest,
    tableInfo
    // { ...tableInfo, planId: state?.planId ?? '' }
    // planName, for dynamic we dont need to pass these as we can get it from tableInfo
    // grantType
  );
  // console.log('table info', tableInfo);

  const { data: grantPlanOptions } = useGetGrantPlanTypeList(handleRequest, initialData.planId);
  const [open, setOpen] = useState(false);
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Grant');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const importExcel = (event: any): void => {
    // setImportLoading(true);

    const payload = {
      File: event.target.files[0],
    };
    handleRequest(GrantService.postApiAwardApiAppGrantImportGrants(payload as any))
      .then((data: any) => {
        if (data) {
          toast.success(
            <ToastComponent label='Success' message='Grant Template import job submitted.' />
          );
          queryClient.invalidateQueries('getPlanManagementList');
          setImportLoading(false);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        setImportLoading(false);
      })
      .finally(() => {
        setImportLoading(false);
      });

    // event.target.value = null;
  };
  const downloadTemplate = (name: string, id: string): void => {
    handleRequest(GrantService.postApiAwardApiAppGrantDownloadGrantImportSample())
      .then((data: any) => {
        if (data != null) {
          handleOpenFile(`${data?.name}.xlsx`, data.content);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Grant',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const handleChecked = (values: string[]): void => {
    setCheckedValues(values);
  };

  const handleBulkExport = (grantIds: string[]): void => {
    const transformedFilter = {
      ...tableInfo,
      deselectedGrantIds: uncheckedIds,
    };
    if (isSelectAllActive) {
      handleRequest(
        GrantFilterService.postApiAwardApiAppGrantFilterBulkExportGrantByFilter(transformedFilter)
      )
        .then((data: any) => {
          if (data != null) {
            if (data.sentToBackground === true) {
              openSubmitModal();
              setTaskReferenceNo(data.taskReferenceNo);
            } else {
              handleOpenFile(data?.name ?? 'Export Grants.xlsx', data.content);
            }
            setCheckedValues([]);
          }
        })
        .catch((error: any) => {
          console.log('error', error);
        });
    } else {
      handleRequest(
        GrantService.postApiAwardApiAppGrantGetExportGrant({
          grantIds: grantIds as string[],
        })
      )
        .then((data: any) => {
          if (data != null) {
            if (data.sentToBackground === true) {
              openSubmitModal();
              setTaskReferenceNo(data.taskReferenceNo);
            } else {
              handleOpenFile(data?.name ?? 'Export Grants.xlsx', data.content);
            }
            setCheckedValues([]);
          }
        })
        .catch((error: any) => {
          console.log('error', error);
        });
    }
  };

  const onViewDetails = (): void => {
    navigate('/task-management-console');
  };

  const tableActions = [
    {
      id: 'view',
      action: 'View',
      onClick: (id: string) => {
        setModalStatus('View');
        getGrantViewData(id);
      },
    },
    {
      id: 'edit',
      action: 'Edit',
      onClick: (id: string) => {
        setModalStatus('Edit');
        getGrantViewData(id);
        // updateGrantViewData(id);
      },
    },
    {
      id: 'delete',
      action: <div className='delete-label'>Delete</div>,
      onClick: (id: string) => {
        setModalStatus('Delete');
        setSelectedGrant(id);
        openConfirmModal();
      },
    },
  ];

  useEffect(() => {
    if (exception != null && modalStatus === 'Add') {
      if (exception?.body?.error as boolean) {
        navigate('page-not-found');
      }
    }
  }, [exception, grantList]);

  const getGrantViewData = (id: string): void => {
    handleRequest(GrantService.getApiAwardApiAppGrantGrantById(id))
      .then((data: any) => {
        if (data != null) {
          const filteredData = {
            id: data.id,
            grantName: data.grantName,
            planTypeId: data.planTypeId,
            planId: data.planId,
            planTypeName: data?.planTypeId,
            acceptanceMethodId: data?.acceptanceMethodId,
            acceptanceMethod: data?.acceptanceMethod,
            poolId: data?.poolId,
            performanceRuleId: data.performanceRuleId,
            performanceRuleName: data.performanceRuleId,
            vestingScheduleId: data.vestingScheduleId,
            vestingScheduleName: data.vestingScheduleId,
            grantAgreementDocumentName: data?.grantAgreementDocumentId,
            grantNoticeDocumentName: data?.grantNoticeDocumentId,
            agreementStatement: data?.agreementStatement,
            agreementStatementStatusName:
              data?.agreementStatementStatusName === 'active' ? 'Active' : 'InActive',
            vestingTemplateName: data?.vestingScheduleId,
            termsAndConditionDocumentName: data?.termsAndConditionDocumentId,
            termsAndConditionTextId: data?.termsAndConditionTextId,
            termsAndConditionTextName: data?.termsAndConditionTextId,
            approvalRequired: data?.approvalRequired,
            planGrantDocuments: data?.planGrantDocuments?.map((doc: any) => ({
              ...doc,
              isDefault: true,
            })),
            planType: grantPlanOptions.find((item: any) => item.value === data?.planTypeId)?.label,
            settlementTypeId: data?.settlementTypeId,
            grantDocument: [],
            // planTypeName: data.planTypeName,
            grantAcceptanceTemplateId: data?.grantAcceptanceTemplateId,
            grantAcceptanceTemplateName: data?.grantAcceptanceTemplateName,
            uniquePlanNo: data?.uniquePlanNo,
            planName: data?.planName,
            isGrantInUse: data?.grantInUse,
            documentNameList: data.documentNameList?.map((item: any) => {
              const fileName = item.grantDocumentFileName;
              return {
                file: {
                  name: item.grantDocumentFileName,
                  uniqueName: item.grantDocumentUniqueName,
                  type: SUPPORTED_FORMATS.find((item) => item.type === getFileExtension(fileName))
                    ?.format,
                },
                id: item.grantDocumentUniqueName,
                src: '',
              };
            }),
            // grantTermsAndConditionsId: data?.grantTermsAndConditionsId,
            approvedDate: data?.approvedDate,
            termsAndConditionsDocument: [],
            // isActive: data.isActive === true ? 'Active' : 'InActive',
            termsAndConditionsAgreementStatement: data.termsAndConditionsAgreementStatement,
            termsAndConditionsDocumentContent: data.termsAndConditionsDocumentContent,
            // termsAndConditionsDocumentName: [
            //   {
            //     file: {
            //       name: data.termsAndConditionsDocumentName?.termsAndConditionsDocumentFileName,
            //       uniqueName:
            //         data.termsAndConditionsDocumentName?.termsAndConditionsDocumentUniqueName,
            //     },
            //   },
            // ],
          };

          setInitialData({
            id,
            data: filteredData as any,
            planId: data?.planId,
          });
          openModal();
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  // const updateGrantViewData = (id: string): any => {
  //   handleRequest(GrantService.getApiAwardApiAppGrantGrantByIdForUpdate(id))
  //     .then((data: any) => {
  //       if (data != null) {
  //         const filteredData = {
  //           id: data.id,
  //           grantName: data.grantName,
  //           planTypeId: data.planTypeId,
  //           performanceRuleId: data.performanceRuleId,
  //           vestingScheduleId: data.vestingScheduleId,
  //           vestingScheduleName: data.vestingScheduleName,
  //           grantDocument: [],
  //           planTypeName: data.planTypeName,
  //           uniquePlanNo: data?.uniquePlanNo,
  //           planName: data?.planName,
  //           isGrantInUse: data?.isGrantInUse,
  //           documentNameList: data.documentNameList?.map((item: any) => {
  //             const fileName = item.grantDocumentFileName;
  //             return {
  //               file: {
  //                 name: item.grantDocumentFileName,
  //                 uniqueName: item.grantDocumentUniqueName,
  //                 type: SUPPORTED_FORMATS.find((item) => item.type === getFileExtension(fileName))
  //                   ?.format,
  //               },
  //               id: item.grantDocumentUniqueName,
  //               src: '',
  //             };
  //           }),
  //           approvedDate: data?.approvedDate,
  //           termsAndConditionsDocument: [],
  //           isActive: data.isActive === true ? 'Active' : 'InActive',
  //           termsAndConditionsAgreementStatement: data.termsAndConditionsAgreementStatement,
  //           termsAndConditionsDocumentContent: data.termsAndConditionsDocumentContent,
  //           termsAndConditionsDocumentName: [
  //             {
  //               file: {
  //                 name: data.termsAndConditionsDocumentName?.termsAndConditionsDocumentFileName,
  //                 uniqueName:
  //                   data.termsAndConditionsDocumentName?.termsAndConditionsDocumentUniqueName,
  //               },
  //             },
  //           ],
  //         };
  //         setInitialData({ id, data: filteredData as any, planId: data?.planId });
  //         openModal();
  //       }
  //     })
  //     .catch((error: any) => {
  //       console.log('error', error);
  //     });
  // };

  const handleDelete = (): void => {
    setLoading(true);
    handleRequest(GrantService.deleteApiAwardApiAppGrantGrant(selectedGrant))
      .then((data: any) => {
        if (data != null) {
          toast.success(<ToastComponent label='Success' message='Grant deleted' />);
          closeConfirmModal();
          void queryClient.invalidateQueries('getGrantList');
          void queryClient.invalidateQueries('getPlanManagementList');
          setLoading(false);
        } else {
          setLoading(false);
          closeConfirmModal();
        }
      })
      .catch(() => {
        setLoading(false);
        closeConfirmModal();
      });
  };
  const { data: companyName } = useGetCustomerCompanyName(customerHandleRequest);
  const [controls, setControls] = React.useState({
    planId: {
      value: state?.planId ?? '',
    },
  });

  const tableHeaderList = GrantManagementHeader({ actions: schemaData?.columns, tableActions })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const grantHeader = (): any[] => {
    return GrantManagementHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: grantHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  const renderRightToolbarButton = () => {
    return (
      <Button
        // as='Create'
        variant={'primary'}
        title={'Add Grant'}
        icon={'add_circle_outline'}
        id='addGrant'
        onClick={() => {
          openModal();
          setModalStatus('Add');
          setInitialData({ ...initialValues, planId: planID });
        }}
      />
    );
  };

  const downloadAndImportTemplate = [
    {
      title: 'Download Template',
      download: downloadTemplate,
      id: 'downloadTemplate',
    },
    {
      title: 'Import Bulk XLS',
      import: importExcel,
      id: 'importBulk',
    },
  ];

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <div data-page='grantManagement' id='pageContainer'>
      <div id='pageContent'>
        {/* <GoBackBlock
          onClick={() => {
            navigate('/settings/organization-settings/plan-management');
          }}
          id='goBackButton'>
          <span className='material-symbols-outlined' id='backIcon'>
            arrow_back
          </span>
          <Heading className='goBack' variant='body-s' title='Go back' id='goBackText' />
        </GoBackBlock> */}
        <SettingSystemUserHeader id='pageHeader'>
          <Heading variant='h3' title='Grant List' id='pageTitle' />
          <Heading
            variant='body-s'
            title={`List of ${companyName ? String(companyName ?? '') : ''
              }’s equity grants on Wealthlane system.`}
            color={`${COLORS.Gray}`}
            id='pageDescription'
          />
        </SettingSystemUserHeader>

        {/* <div className='flex gap-x-4 mb-10'> */}
        {/* <div className='col-span-1 md:col-span-1 lg:col-span-1 '>
            <Filter setOpen={setOpen} open={open}>
              <Heading variant='body-xs' title={'Filter By:'} className={'filter-label'} />
              <div className='grid sm:grid-cols-2 gap-x-5'>
                <FormSelect
                  type='text'
                  name='planName'
                  placeholder='Plan Name'
                  viewName='customerRole'
                  onChange={(value: string) => {
                    setTableInfo(InitialValuesForm.tableParams);
                    setPlanName(value);
                  }}
                  options={planNames}
                  values={planName}
                  arrValue={planName}
                  containerClassName='formGroup'
                  isFormik={false}
                  required
                />
                <FormSelect
                  type='text'
                  name='grantType'
                  placeholder='Grant Type'
                  onChange={(value: string) => {
                    setTableInfo(InitialValuesForm.tableParams);
                    setGrantType(value);
                  }}
                  options={grantPlanOptions}
                  values={grantType}
                  arrValue={grantType}
                  containerClassName='formGroup'
                  isFormik={false}
                  required
                />
              </div>
            </Filter>
          </div> */}
        {/* <div className='sm:col-span-2 lg:col-span-2 '>
            <FormSelect
              type='text'
              name='planName'
              placeholder='Plan Name'
              viewName='customerRole'
              onChange={(value: string) => {
                setTableInfo(InitialValuesForm.tableParams);
                setPlanName(value);
              }}
              options={planNames}
              values={planName}
              arrValue={planName}
              containerClassName='formGroup'
              isFormik={false}
              required
            />
          </div>
          <div className='sm:col-span-2 lg:col-span-2'>
            <FormSelect
              type='text'
              name='grantType'
              placeholder='Grant Type'
              onChange={(value: string) => {
                setTableInfo(InitialValuesForm.tableParams);
                setGrantType(value);
              }}
              options={grantPlanOptions}
              values={grantType}
              arrValue={grantType}
              containerClassName='formGroup'
              isFormik={false}
              required
            />
          </div> */}

        {/* <div className='sm:col-span-2 lg:col-span-2'>
            <ResetFilterWrapper
              onClick={() => {
                setPlanName('');
                setGrantType('');
                setTableInfo(InitialValuesForm.tableParams);
              }}>
              <span className='material-symbols-outlined'>settings_backup_restore</span>
              <Heading variant='body-s' title='Reset Filter' />
            </ResetFilterWrapper>
          </div> */}
        {/* <SettingSystemUserButton className='col-span-2 flex justify-end'>
            <Button
              as='Create'
              variant={'primary'}
              title={'Add Grant'}
              icon={'add_circle_outline'}
              onClick={() => {
                setInitialData({ ...initialValues, planId: planID });
                setModalStatus('Add');
                openModal();
              }}
            />
          </SettingSystemUserButton> */}
        {/* </div> */}
        {/* <div className='flex items-center gap-x-5'>
          <Button
            variant={'primary'}
            title={'Add a Grant'}
            onClick={() => {
              setInitialData({ ...initialValues, planId: planID });
              setModalStatus('Create');
              openModal();
            }}
          />
        </div> */}
      </div>
      <div className='overflow-x-auto' id='tableContainer'>
        {/* <Table
          columns={GrantManagementHeader(actions)}
          data={grantList?.items ?? []}
          loading={status}
          // tableInfo={InitialValuesForm.tableParams}
          showPagination
          tableInfo={tableInfo}
          setTableInfo={setTableInfo}
          totalCounts={grantList?.totalCount ?? 10}
        /> */}
        <DynamicTable
          schema={schema}
          data={grantList?.items ?? []}
          totalCounts={grantList?.totalCount ?? 10}
          loading={status}
          filterControls={controls}
          tableColumnList={tableHeaderList}
          selectedColumnList={headerList}
          tablePreferenceLoading={tablePrefLoading}
          handleColumnSave={handleSavePreference}
          onChecked={handleChecked}
          filteredKey={'id'}

          checkedValues={checkedValues}
          showCheckBox={true}
          showBulkExport={checkedValues?.length > 0}
          handleExportBulk={() => handleBulkExport(checkedValues)}
          checkedItems={checkedItems}
          setCheckedItems={setCheckedItems}
          showSelectAllButton={true}
          id='grantManagementTable'
          templateDatas={downloadAndImportTemplate}
          components={{
            renderRightToolbarButton,
          }}
        />
      </div>

      {isConfirmOpen && (
        <AlertPopup
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={'this grant'}
          handleSubmit={handleDelete}
          loading={loading}
          id='grantAlert'
        />
      )}
      {isModalOpen && (
        <GrantManagementModal
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          modalStatus={modalStatus}
          setModalStatus={setModalStatus}
          initialData={initialData}
          id='grantModal'
        />
      )}
      {isSubmitOpen && (
        <AlertPopup
          isShow={isSubmitOpen}
          closeModal={undefined}
          taskRefrenceNo={taskReferenceNo}
          handleSubmit={onViewDetails}
          customTitle={'Your request has been submitted for processing.'}
          ConfirmTitle={'View Details'}
          icon={false}
          showTaskReferenceNo={true}
          display={true}
          dynamicReport={false}
        />
      )}
    </div>
  );
};

export default withDynamicTableContext(GrantManagement, false, axiosInstance);
